import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Vault, ArrowUpRight, ArrowDownRight, ShieldCheck, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import Onboarding from "@/components/Onboarding";

const ONBOARDING_KEY = "byto_onboarding_completed";

export default function Login() {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      setShowOnboarding(true);
    }
  }, []);

  const finishOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "1");
    setShowOnboarding(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background byto-grain flex flex-col">
      {showOnboarding && <Onboarding onFinish={finishOnboarding} />}

      <header className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Vault className="w-5 h-5 text-emerald-500" />
          </div>
          <span className="text-xl font-bold tracking-tight"><span className="text-emerald-500">By</span><span className="text-rose-500">to</span></span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reopen-onboarding-button"
            onClick={() => setShowOnboarding(true)}
            className="w-10 h-10 rounded-xl border border-border bg-secondary/60 flex items-center justify-center hover:bg-accent transition-colors"
            aria-label="Ver introducción"
          >
            <HelpCircle className="w-[18px] h-[18px] text-muted-foreground" />
          </button>
          <ThemeToggle />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="grid lg:grid-cols-2 gap-16 max-w-5xl w-full items-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Finanzas Personales
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              El control de <br />
              <span className="text-emerald-500">tu dinero.</span>
            </h1>
            <p className="text-muted-foreground text-base sm:text-lg mt-6 max-w-md leading-relaxed">
              Controla el origen y el destino de cada peso. ByTo utiliza <span className="text-emerald-500 font-semibold">By</span> para ingresos y <span className="text-rose-500 font-semibold">To</span> para gastos.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <div className="flex-1 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-4">
                <div className="flex items-center gap-2 text-emerald-500 font-semibold">
                  <ArrowUpRight className="w-5 h-5" strokeWidth={2.4} /> By
                </div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-2">Origen de los fondos</p>
              </div>
              <div className="flex-1 rounded-2xl border border-rose-500/20 bg-rose-500/[0.06] p-4">
                <div className="flex items-center gap-2 text-rose-500 font-semibold">
                  <ArrowDownRight className="w-5 h-5" strokeWidth={2.4} /> To
                </div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-2">Destino del dinero</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border border-border bg-card p-8 shadow-2xl"
          >
            <h2 className="text-2xl font-bold tracking-tight">Entra a tu bóveda</h2>
            <p className="text-muted-foreground text-sm mt-2 mb-8">
              Inicia sesión con Google. Cada usuario ve únicamente sus propios movimientos.
            </p>
            <Button
              data-testid="google-login-button"
              onClick={handleGoogleLogin}
              className="w-full h-12 rounded-xl text-base gap-3 bg-foreground text-background hover:bg-foreground/90"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continuar con Google
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-6 leading-relaxed">
              Al continuar aceptas administrar tus finanzas de forma segura dentro de Byto.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
